import React, { useCallback, useState } from 'react';
import { inject, observer } from 'mobx-react';
import clsx from 'classnames';
import { InitStore } from '@/stores/init';
import Drawer from '@/components/Drawer';
import { handleRequest } from '@/utils';
import { addUserMsg, postChatMsg, reviewChatEndText } from '@/utils/agent';
import {
  GetMsgResCode,
  INPUT_TYPE,
  TRACKING_MSG_TYPE,
  TRACKING_OPERATION,
  TRACKING_SECTION,
  TRACKING_TARGET,
} from '@/utils/constants';
import { getChildInject, getTrans, isNum } from '@/utils/instantLoaded/tools';
import { AgentStore } from '@/stores/agent';
import { handleClickHistoryButton } from '@/utils/tools';
import { dispatchTrack } from '@/utils/tracking';
import { addDialogue } from '@/utils/sessionUtils';
import uploadMessageToSave, { getMessageUploadKey, MessageUploadStatusMap } from '@/utils/uploadMessageToSave';
import * as IntentSelectorConstants from './constants';
import './style.scss';
import IntentList from './intentList';
import { Intent, SubIntent } from './types';
import { IntentContextStore } from './intentContext';
import { ChatbotUIBasicProps } from '@/types/common';

export type IntentSelectorProps = {
  agentStore: AgentStore;
  initStore: InitStore;
  nodePointId: string;
  dialogueId: string;
  question: string;
  intentList: Intent[];
  historyDialogueId?: string;
  historySessionId?: string;
  isHistory: boolean;
  listPos: number;
  isClarification?: boolean;
  clarification_version?: string;
  gray_option?: IntentSelectorConstants.GRAY_OPTION.CLICKED_GRAY_OUT | IntentSelectorConstants.GRAY_OPTION.ALL_GRAY_OUT;
  thread_id?: string;
  thread_node_id?: string;
  thread_id_v2?: string;
  round_id?: string;
  clarification_stage?: number;
  customCallback?: ChatbotUIBasicProps['customCallback'];
  useUIOptimisationRecommender?: boolean;
};

const IntentSelector = (props: IntentSelectorProps) => {
  const {
    initStore: { sessionId },
    nodePointId,
    dialogueId,
    question,
    intentList,
    isHistory,
    historySessionId,
    historyDialogueId,
    isClarification,
    clarification_version,
    gray_option,
    thread_id,
    thread_node_id,
    thread_id_v2,
    round_id,
    clarification_stage,
    customCallback = {},
    useUIOptimisationRecommender,
  } = props;
  const [showDrawer, setShowDrawer] = useState(false);
  const [subIntents, setSubIntents] = useState({ intentId: -1, intentText: '', subIntentList: [] as SubIntent[] });

  const intentListValue = {
    isHistory,
    historySessionId,
    sessionId,
    historyDialogueId,
    dialogueId,
    nodePointId,
  };

  const trackClick = useCallback(({ button_type, button_id, button_text }) => {
    dispatchTrack({
      operation: TRACKING_OPERATION.CLICK,
      section: TRACKING_SECTION.standardizeButton,
      target: TRACKING_TARGET.standardizeButton,
      data: {
        chat_session_id: isHistory ? historySessionId : sessionId,
        chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
        node_point_id: nodePointId,
        button_type,
        button_id,
        button_text,
        if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      },
    });
  }, []);

  const sendMessageToAgent = useCallback(
    async (text: string) => {
      const { agentStore } = props;
      const { agentEndedReason, inQueue } = agentStore;
      const curPos = addUserMsg(
        {
          from: 'user',
          type: 'Msg',
          data: {
            content: text,
            requesting: true,
          },
        },
        false,
      );

      // 未成功输入
      if (!isNum(curPos)) {
        return;
      }

      // 如果会话已结束
      if (agentEndedReason) {
        reviewChatEndText();
        agentStore.UPDATE_AGENT_DATA_ITEM(
          {
            loading: false,
            requesting: false,
            error: true,
          },
          curPos as number,
        );

        return;
      }

      if (inQueue) {
        agentStore.UPDATE_AGENT_DATA_ITEM(
          {
            loading: false,
            requesting: false,
          },
          curPos as number,
        );
      } else {
        // 已连接 live agent
        try {
          const { code: resCode } = await postChatMsg({ text });

          agentStore.UPDATE_AGENT_DATA_ITEM(
            {
              loading: false,
              requesting: false,
              error: resCode !== GetMsgResCode.SUCCESS,
            },
            curPos as number,
          );
        } catch (error) {
          agentStore.UPDATE_AGENT_DATA_ITEM(
            {
              loading: false,
              requesting: false,
              error: true,
            },
            curPos as number,
          );
        }
      }
    },
    [props.agentStore],
  );

  const sendMessage = useCallback(
    async (subIntent: SubIntent, intentText?: string) => {
      const { agentStore } = props;
      const { inAgent } = agentStore;
      const { sub_intent_text, button } = subIntent;
      const {
        node_id,
        callback: { message_extra: { params = {}} = {}} = {},
        extra_param,
        intent_base_action,
      } = button || {};
      const hasIntentText = intentText !== undefined && intentText !== '';

      if (inAgent) {
        if (hasIntentText) {
          await sendMessageToAgent(intentText as string);
        }

        await sendMessageToAgent(sub_intent_text as string);
      } else {
        // 走 ChatBot 逻辑
        const curPos = addDialogue(
          [
            {
              from: 'user',
              type: 'Msg',
              data: { content: intentText },
            },
            {
              from: 'user',
              type: 'Msg',
              data: { content: sub_intent_text },
            },
          ].filter((msg) => msg.data.content),
          false,
        );

        if (hasIntentText) {
          const messageUploadKey = getMessageUploadKey([intentText] as string[]);

          uploadMessageToSave({
            dialogueId,
            messages: [
              {
                message_text: intentText,
                is_sent_by_user: true,
              },
            ],
            key: messageUploadKey,
          });

          await MessageUploadStatusMap[messageUploadKey];
        }

        if (isNum(curPos)) {
          const message_extra: any = {
            node_id,
            params: { ...params },
            is_params: { ...extra_param },
            intent_base_action,
            clarification_stage,
          };

          clarification_version && (message_extra.clarification_version = clarification_version);

          await handleRequest('postMessage', {
            session_id: sessionId,
            dialogue_id: dialogueId,
            input_type: INPUT_TYPE.BUTTON,
            message_text: sub_intent_text,
            message_extra,
            thread_id,
            thread_node_id,
            thread_id_v2,
            round_id,
            ...customCallback,
          });
        }
      }
    },
    [props.agentStore],
  );

  const handleIntentClick = useCallback(async (index: number) => {
    const { id, intent_text, button_type, button_id, sub_intent_list } = intentList[index];

    trackClick({
      button_type,
      button_id,
      button_text: intent_text,
    });

    if (isHistory) {
      handleClickHistoryButton();
      return;
    }

    if (sub_intent_list?.length === 1) {
      await sendMessage(sub_intent_list[0]);
    } else {
      setSubIntents({
        intentId: id,
        intentText: intent_text,
        subIntentList: sub_intent_list,
      });
      setShowDrawer(true);

      dispatchTrack({
        operation: TRACKING_OPERATION.IMPRESSION,
        section: TRACKING_SECTION.standardizeButton,
        target: TRACKING_TARGET.standardizeButton,
        data: {
          chat_session_id: isHistory ? historySessionId : sessionId,
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          node_point_id: nodePointId,
          button_details: sub_intent_list.map((subIntent) => ({
            button_type: subIntent?.button?.button_type,
            button_id: subIntent.button_id,
            button_text: subIntent.sub_intent_text,
          })),
          if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
      });
    }
  }, []);

  const handleSubIntentClick = useCallback(
    (subIntent: SubIntent) => {
      const { sub_intent_text, button_id, button } = subIntent;
      const { button_type } = button || {};

      trackClick({
        button_type,
        button_id,
        button_text: sub_intent_text,
      });

      setShowDrawer(false);
      sendMessage(subIntent, subIntents.intentText);
    },
    [subIntents],
  );

  const computedClickableType = () => {
    if (isHistory) {
      return IntentSelectorConstants.CLICKABLE_TYPE.ALL_DISABLED;
    }

    if (isClarification) {
      // 处理意图澄清的Intent
      if (typeof gray_option !== 'undefined') {
        return gray_option === IntentSelectorConstants.GRAY_OPTION.ALL_GRAY_OUT
          ? IntentSelectorConstants.CLICKABLE_TYPE.CLICKED_ONCE_TO_DISABLED
          : IntentSelectorConstants.CLICKABLE_TYPE.CLICKED_ITEM_TO_DISABLED;
      }

      if (clarification_version) {
        // 旧逻辑兼容
        return IntentSelectorConstants.CLICKABLE_TYPE.CLICKED_ITEM_TO_DISABLED;
      }
    }

    return IntentSelectorConstants.CLICKABLE_TYPE.ALL_CLICKABLE;
  };

  return (
    <div className={clsx('intent-selector', useUIOptimisationRecommender ? 'ui-optimisation-recommender' : '')}>
      {question && <div className="intent-selector-title">{question}</div>}
      <IntentContextStore.Provider value={intentListValue}>
        <IntentList
          clickableType={computedClickableType()}
          intentList={intentList}
          handleIntentClick={handleIntentClick}
          useUIOptimisationRecommender={useUIOptimisationRecommender}
        />
      </IntentContextStore.Provider>

      <Drawer
        show={showDrawer}
        onClose={() => {
          setShowDrawer(false);
        }}
        height="auto"
      >
        <div className="sub-intent-select">
          <div className="select-title">{getTrans('btn_choose_issue')}</div>
          <div className="sub-intent-list">
            {subIntents.subIntentList.map((subIntent) => (
              <div
                className="sub-intent-item"
                key={subIntent.sub_intent_node_point_id}
                onClick={() => {
                  handleSubIntentClick(subIntent);
                }}
              >
                {subIntent.sub_intent_text}
              </div>
            ))}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default inject(getChildInject('initStore', 'agentStore'))(observer(IntentSelector));
